import PropTypes from 'prop-types';
import { Component } from 'react';
import { FormattedMessage, IntlProvider } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  selectCurrentLanguageCode,
  selectCurrentLanguageId,
} from '!core/features';
import { expressions, getBrowserLocales } from '!i18n/index';
import { isAureaActiveClient } from '»utils/clientsUtils';

import AureaErrorImg from '../assets/img/AUREA_error-page.svg';
import FinanetchErrorImg from '../assets/img/FINANTECH_error-page.svg';
import { reportError } from './errorBoundary.services';
import {
  LogoImgStyled,
  MainContainer,
  TextStyled,
} from './ErrorBoundary.styles';

const ErrorFallback = () => {
  const languageId = useSelector(selectCurrentLanguageId);
  const languageCode = useSelector(selectCurrentLanguageCode);

  const browserLang = getBrowserLocales()[0].includes('pt')
    ? { id: 'PRT', code: 'pt-PT' }
    : { id: 'ENG', code: 'en-US' };

  const currentLanguageId = languageId ?? browserLang.id;
  const currentLanguageCode = languageCode ?? browserLang.code;

  const getErrorImage = () => {
    if (isAureaActiveClient) return AureaErrorImg;

    return FinanetchErrorImg;
  };

  return (
    <IntlProvider
      defaultLocale={currentLanguageCode}
      locale={currentLanguageCode}
      key={currentLanguageId}
      messages={expressions[currentLanguageId]}
    >
      <MainContainer>
        <LogoImgStyled src={getErrorImage()} />
        <TextStyled>
          <FormattedMessage id="TRLT_UI-ERROR_MSG" />
        </TextStyled>
      </MainContainer>
    </IntlProvider>
  );
};

ErrorFallback.propTypes = {
  languageId: PropTypes.string,
  languageCode: PropTypes.string,
};

class ErrorBoundaryCore extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error, info) {
    this.setState({ hasError: true });

    reportError(error, info);
  }

  render() {
    const { hasError } = this.state;
    const { children } = this.props;

    if (hasError) return <ErrorFallback />;

    return children;
  }
}

ErrorBoundaryCore.propTypes = {
  children: PropTypes.element.isRequired,
};

const ErrorBoundary = (props) => <ErrorBoundaryCore {...props} />;

ErrorBoundary.propTypes = {
  children: PropTypes.element.isRequired,
};

export { ErrorBoundary };
